import { flexRender } from '@tanstack/react-table';
import styled from '@emotion/styled';
import { ArrowDown, ArrowUp, PushPinSimple, Check, CaretUpDown } from '@phosphor-icons/react';
import { useTableContext } from '../providers/TableProvider';
import { DotsMenuButton } from './DotsMenuButton';
import { useTheme } from '@emotion/react';
import { t } from '@lingui/macro';
import { Box } from '@mui/material';
import { Text } from 'components/text/Text';
import { trans } from 'features/misc/table/utils';
import { colors } from 'styles/theme/colors';
import { styledTheme } from 'styles/theme/theme';

export type TableHeadProps = {
  columnResizeMode: string;
  rowHeight: number;
  isExpandable?: boolean;
};

export const TableHead = ({ columnResizeMode, rowHeight, isExpandable }: TableHeadProps) => {
  const { table, config } = useTableContext();
  const theme = useTheme();
  return (
    <THead rowHeight={rowHeight}>
      {table.getHeaderGroups().map(headerGroup => (
        <tr key={headerGroup.id}>
          {headerGroup.headers.map((header, index) => {
            const isFirstCell = 1 === index;
            const isFrozen = header.column.getIsPinned();
            const left = isFrozen ? header?.column?.getStart('left') : 0;
            const isSelector = header.id === 'Selector';
            const border = header.column.columnDef.border;

            const handleSortClick = () => {
              if (!isSelector && header.column.getCanSort()) {
                header.column.toggleSorting();
              }
            };

            const menuOptions = [
              {
                label: t`Pin`,
                endIcon: header.column?.getIsPinned() ? (
                  <Check size={16} color={theme.palette.text.brandTertiary} />
                ) : undefined,
                icon: <PushPinSimple size={16} />,
                onClick: () => {
                  if (header.column?.getIsPinned()) {
                    header.column.pin(false);
                    return;
                  }
                  header?.column?.pin('left');
                },
              },
            ];
            let headerClassName = '';
            if (header.column.getCanSort()) {
              headerClassName = 'sortable';
            }
            if (isSelector) {
              headerClassName = 'selector';
            }
            return (
              <th
                key={header.id}
                colSpan={header.colSpan}
                className={isFrozen ? 'frozen' : ''}
                style={{
                  ...(isExpandable && isSelector
                    ? { borderRight: `0.5px solid ${colors.grey[200]}` }
                    : {}),
                  left: isFrozen ? left : 'unset',
                  width: header.getSize(),
                  minWidth: header.getSize(),
                  ...(isFirstCell ? { flexGrow: 1 } : {}),
                  ...(border === 'left' && {
                    borderLeft: `0.5px solid ${styledTheme.palette.border.primarySubtle}`,
                  }),
                  ...(border === 'right' && {
                    borderRight: `0.5px solid ${styledTheme.palette.border.primarySubtle}`,
                  }),
                  ...(border === 'both' && {
                    borderLeft: `0.5px solid ${styledTheme.palette.border.primarySubtle}`,
                    borderRight: `0.5px solid ${styledTheme.palette.border.primarySubtle}`,
                  }),
                }}
              >
                {header.isPlaceholder ? null : (
                  <HeaderDiv className={headerClassName}>
                    <HeaderText
                      variant="bodySm"
                      weight="semiBold"
                      color="inherit"
                      onClick={handleSortClick}
                    >
                      {trans(flexRender(header.column.columnDef.header, header.getContext()))}{' '}
                      {header?.column?.columnDef?.meta?.renderCount
                        ? header?.column?.columnDef?.meta?.renderCount()
                        : header?.column?.columnDef?.meta?.showCount
                          ? `(${table?.getRowCount()})`
                          : null}
                    </HeaderText>
                    <HeaderActions>
                      {header.column.getCanSort() &&
                        (header.column.getIsSorted() === 'asc' ? (
                          <ArrowUp
                            data-testid="arrow-up"
                            color={theme.palette.fg.secondary}
                            size={16}
                            style={{ cursor: 'pointer' }}
                            onClick={handleSortClick}
                          />
                        ) : header.column.getIsSorted() === 'desc' ? (
                          <ArrowDown
                            data-testid="arrow-down"
                            color={theme.palette.fg.secondary}
                            size={16}
                            style={{ cursor: 'pointer' }}
                            onClick={handleSortClick}
                          />
                        ) : (
                          <HeaderSortActions className="headerSortActions">
                            <CaretUpDown
                              data-testid="caret-up-down"
                              color={theme.palette.fg.secondary}
                              size={16}
                              style={{ cursor: 'pointer' }}
                              onClick={handleSortClick}
                            />
                          </HeaderSortActions>
                        ))}
                      {header.id === 'Selector' ? null : (
                        <Box minWidth="18px">
                          <DotsMenuButton menuOptions={menuOptions} />
                        </Box>
                      )}
                    </HeaderActions>

                    {config?.row?.resizeable &&
                      header.column.getCanResize() &&
                      header.id !== 'Selector' && (
                        <div
                          {...{
                            onMouseDown: header.getResizeHandler(),
                            onTouchStart: header.getResizeHandler(),
                            className: `resizer ${table.options.columnResizeDirection} ${
                              header.column.getIsResizing() ? 'isResizing' : ''
                            }`,
                            style: {
                              transform:
                                columnResizeMode === 'onEnd' && header.column.getIsResizing()
                                  ? `translateX(${
                                      (table.options.columnResizeDirection === 'rtl' ? -1 : 1) *
                                      (table.getState().columnSizingInfo.deltaOffset ?? 0)
                                    }px)`
                                  : '',
                            },
                          }}
                        />
                      )}
                  </HeaderDiv>
                )}
              </th>
            );
          })}
        </tr>
      ))}
    </THead>
  );
};

const HeaderText = styled(Text)`
  cursor: pointer;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 5px;
`;

const HeaderDiv = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 24px;
  .dotsMenuBtn {
    all: unset;
    cursor: pointer;
    display: flex;
  }
  .dotsMenu {
    display: none;
  }
  .headerSortActions {
    display: none;
  }
  :hover {
    .dotsMenu {
      display: flex;
    }
    .headerSortActions {
      display: flex;
    }
  }
`;

const HeaderActions = styled.div`
  display: flex;
`;

const HeaderSortActions = styled.div`
  display: flex;
`;

export const THead = styled.thead<{ rowHeight: number }>`
  input[type='checkbox'] {
    display: initial;
  }

  position: sticky;
  top: 0;
  z-index: 3;
  tr {
    width: 100%;
    border-bottom: 0.5px solid ${p => p.theme.palette.border.brand};
  }

  th {
    font-size: 0.875rem;
    padding: 0;
    height: ${p => p.rowHeight}px;
    color: ${p => p.theme.palette.text.secondary};
    background-color: ${p => p.theme.palette.bg.brandSubtle};
    top: 0;
    min-height: ${p => p.rowHeight}px;

    :hover {
      background-color: ${p => p.theme.palette.bg.brandPrimary};
    }

    & > div {
      height: 100%;
      position: relative;
      display: flex;
      align-items: center;

      span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    & .sortable {
      svg {
        color: ${p => p.theme.palette.fg.primary};
      }
    }
    & > div.selector {
      padding: 8px 15px;
    }
    th span {
      order: 1;
    }

    th svg {
      order: 2;
    }

    th svg ~ span {
      font-weight: 700;
      color: ${p => p.theme.palette.fg.primary};
    }

    .resizer {
      position: absolute;
      top: 0;
      height: 100%;
      width: 3px;
      cursor: col-resize;
      user-select: none;
      touch-action: none;
      opacity: 0;
      background: ${p => p.theme.palette.text.quaternaryOnBrand};
      :hover {
        opacity: 1;
      }
    }

    .resizer.ltr {
      right: 0;
    }

    .resizer.rtl {
      left: 0;
    }

    .resizer.isResizing {
      background: ${p => p.theme.palette.text.quaternaryOnBrand};
      opacity: 1;
    }
  }
`;
